import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { IMember, IPresence, IRespondent } from "./types"
import api from "services/api"
import { IFeedbackForm } from "./feedback"
import { IPresenceForm } from "./presence"

export function useMembers(
  event_id: string,
  config?: UseQueryOptions<IPresence[]>
) {
  return useQuery<IPresence[]>(
    ["operation", "production", "events", event_id, "members"],
    config
  )
}

export function useMember(id?: string, config?: UseQueryOptions<IMember>) {
  return useQuery<IMember>(["operation", "administration", "members", id], {
    ...config,
  })
}

export function useExecutionMembers(
  event_id: string,
  config?: UseQueryOptions<IPresence[]>
) {
  return useQuery<IPresence[]>(
    ["operation", "production", "events", "execution", event_id, "members"],
    config
  )
}

export interface IApplyRequestBody {
  formData: IPresenceForm
  presenceId: string
}

export function useApplyTransition(
  eventId: string,
  config?: UseMutationOptions<unknown, unknown, IApplyRequestBody, unknown>
) {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, IApplyRequestBody, unknown>(
    ({ presenceId, formData }) =>
      api.post(
        `operation/production/events/execution/${eventId}/members/${presenceId}/applyTransition`,
        formData
      ),
    {
      ...config,
      onSuccess(data, variables) {
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "execution",
          eventId,
          "members",
          variables.presenceId,
          "getTransitions",
        ])
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "execution",
          eventId,
          "members",
        ])
      },
    }
  )
}

export function useExecutionSubscribableMembers(
  eventId: string,
  config?: UseQueryOptions<IMember[]>
) {
  return useQuery<IMember[]>(
    [
      "operation",
      "production",
      "events",
      "execution",
      eventId,
      "member-presences",
    ],
    {
      ...config,
    }
  )
}

export function useExecutionSubscribableEntenteParticipants(
  eventId: string,
  config?: UseQueryOptions<IMember[]>
) {
  return useQuery<IMember[]>(
    [
      "operation",
      "production",
      "events",
      "execution",
      eventId,
      "entente-participants",
    ],
    {
      ...config,
    }
  )
}

export function useSubscribeMember(
  eventId: string,
  config?: UseMutationOptions<unknown, unknown, unknown, unknown>
) {
  const queryClient = useQueryClient()

  return useMutation<unknown, unknown, unknown, unknown>(
    (memberId) =>
      api.post(
        `operation/production/events/execution/${memberId}/members/${eventId}/toSubscription`
      ),
    {
      ...config,
      onSuccess(data, vars, context) {
        config?.onSuccess?.(data, vars, context)
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "execution",
          eventId,
          "members",
        ])
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "execution",
          eventId,
          "member-presences",
        ])
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "execution",
          eventId,
          "entente-participants",
        ])
      },
    }
  )
}

export function useUnsubscribeMember(
  eventId: string,
  config?: UseMutationOptions<unknown, unknown, unknown, unknown>
) {
  const queryClient = useQueryClient()

  return useMutation<unknown, unknown, unknown, unknown>(
    (memberId) =>
      api.post(
        `operation/production/events/execution/${memberId}/members/${eventId}/toUnsubscribe`
      ),
    {
      ...config,
      onSuccess(data, vars, context) {
        config?.onSuccess?.(data, vars, context)
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "execution",
          eventId,
          "members",
        ])
        queryClient.invalidateQueries([
          "operation",
          "administration",
          "members",
          "events",
          eventId,
          "members",
        ])
      },
    }
  )
}

export function useCreateFeedback(
  event_id: string,
  member_id: string,
  config?: UseMutationOptions<unknown, unknown, IFeedbackForm>
) {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, IFeedbackForm>(
    (data) =>
      api.put(
        `operation/production/events/execution/${event_id}/members/${member_id}/feedback`,
        data
      ),
    {
      ...config,
      onSuccess() {
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "execution",
          event_id,
          "members",
        ])
      },
    }
  )
}

export function useExecutionMember(
  eventId: string,
  presenceId: string,
  config?: UseQueryOptions<IPresence>
) {
  return useQuery<IPresence>(
    [
      "operation",
      "production",
      "events",
      "execution",
      eventId,
      "members",
      presenceId,
    ],
    {
      ...config,
    }
  )
}

export function useEventMember(
  eventId: string,
  memberId: string,
  config?: UseQueryOptions<IPresence>
) {
  return useQuery<IPresence>(
    [
      "operation",
      "production",
      "events",
      "external",
      eventId,
      "members",
      memberId,
    ],
    {
      ...config,
    }
  )
}

export function useExecutionDiagnosticForm(
  eventId: string,
  memberId: string,
  config?: UseQueryOptions<any>
) {
  return useQuery<any>(
    [
      "operation",
      "production",
      "events",
      "execution",
      eventId,
      "members",
      memberId,
      "diagnostic",
    ],
    config
  )
}

export function useDiagnosticForm(
  eventId: string,
  memberId: string,
  config?: UseQueryOptions<any>
) {
  return useQuery<any>(
    [
      "operation",
      "production",
      "events",
      eventId,
      "members",
      memberId,
      "diagnostic",
    ],
    config
  )
}

export function useMemberRespondents(
  memberId: string,
  config?: UseQueryOptions<IRespondent[]>
) {
  return useQuery<IRespondent[]>(
    [
      "operation",
      "administration",
      "members",
      "external",
      memberId,
      "respondents",
    ],
    {
      ...config,
      select(res) {
        let data = res.filter((element) => !element.is_institutional)
        return data
      },
    }
  )
}

export function useToggleAbsence(
  event_id: string,
  config?: UseMutationOptions<
    unknown,
    unknown,
    { reason?: string; member_id: string },
    unknown
  >
) {
  const queryClient = useQueryClient()
  return useMutation<
    unknown,
    unknown,
    { reason?: string; member_id: string },
    unknown
  >(
    ({ reason, member_id }) =>
      api.post(
        `operation/production/events/execution/${event_id}/members/${member_id}/toggle-absence`,
        { reason }
      ),
    {
      ...config,
      onSuccess(data, vars, context) {
        config?.onSuccess?.(data, vars, context)
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "execution",
          event_id,
          "members",
        ])
      },
    }
  )
}
